import { Grid } from '@mui/material';
import { memo } from 'react';
import { AccordionFamilyCoexistence as AccordionFamilyCoexistenceVM } from 'shell/view-models/accordion-family-coexistence';
import TableFamily from './table-family';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { FamilyVM } from 'shell/view-models/family';
import { DateTime } from 'luxon';
import { transformGenerality } from 'shell/utils/consult-person/response-transformer/registry-office/generality';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';

interface IdentityCardProps {
  disabled?: boolean;
  values?: AccordionFamilyCoexistenceVM;
  generality?: AccordionGeneralityForm;
  onChange: (
    key: keyof AccordionFamilyCoexistenceVM,
    value: AccordionFamilyCoexistenceVM[keyof AccordionFamilyCoexistenceVM]
  ) => void;
}

const FamilyCoexistenceListComponent = ({
  values,
  generality: generalityActual,
}: IdentityCardProps): React.ReactElement => {
  const [familyMembers] = useHttpFetch<HttpResponse<ResponseAnprData>, Array<FamilyVM>>(
    `${__PERSON_NEW_API_URL__}/persona/ricerca-famiglia`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            // Fixed value for request family
            tipoScheda: '1',
            identificativoANPR: values?.identifierAnpr || undefined,
          },
          datiRichiesta: {
            schedaAnagraficaRichiesta: '1',
            ricercaNazionale: '0',
            // Fixed value for request family
            datiAnagraficiRichiesti: ['02', '10', '21', '22'],
          },
        },
      },
      transformer: (response) => {
        return response.data.payload?.risposta3002OK?.listaSoggetti?.datiSoggetto
          ?.sort((a, b) => {
            const codiceLegameA = a?.legameSoggetto?.codiceLegame
              ? Number.parseInt(a?.legameSoggetto?.codiceLegame, 10)
              : undefined;
            const codiceLegameB = b?.legameSoggetto?.codiceLegame
              ? Number.parseInt(b?.legameSoggetto?.codiceLegame, 10)
              : undefined;

            const birthDateA = a?.generalita?.dataNascita
              ? DateTime.fromISO(a?.generalita?.dataNascita)
              : null;
            const birthDateB = b?.generalita?.dataNascita
              ? DateTime.fromISO(b?.generalita?.dataNascita)
              : null;

            if (codiceLegameA && codiceLegameB && codiceLegameA < codiceLegameB) {
              return -1;
            }
            if (codiceLegameA && codiceLegameB && codiceLegameA > codiceLegameB) {
              return 1;
            }

            if (
              codiceLegameA &&
              codiceLegameB &&
              codiceLegameA === codiceLegameB &&
              birthDateA &&
              birthDateB &&
              birthDateA < birthDateB
            ) {
              return -1;
            }
            if (
              codiceLegameA &&
              codiceLegameB &&
              codiceLegameA === codiceLegameB &&
              birthDateA &&
              birthDateB &&
              birthDateA > birthDateB
            ) {
              return 1;
            }
            return 0;
          })
          .map((subject) => {
            const generality = transformGenerality(subject);
            const person = transformPerson(subject);
            let birthPlace = '';
            // birthplace check for object
            if (subject.generalita?.luogoNascita?.localita) {
              birthPlace = `${
                subject.generalita?.luogoNascita?.localita.descrizioneLocalita || ''
              } (${subject.generalita?.luogoNascita?.localita.descrizioneStato || ''})`;
            }

            if (subject.generalita?.luogoNascita?.comune) {
              birthPlace = `${subject.generalita?.luogoNascita?.comune.nomeComune || ''} (${
                subject.generalita?.luogoNascita?.comune.siglaProvinciaIstat || ''
              })`;
            }

            return {
              surname: subject.generalita?.cognome || '',
              actual: subject.generalita?.idSchedaSoggettoANPR === generalityActual?.anprIdentifier,
              name: subject.generalita?.nome || '',
              fiscalCode: subject.generalita?.codiceFiscale?.codFiscale || '',
              birthDate: subject.generalita?.dataNascita
                ? DateTime.fromISO(subject.generalita?.dataNascita).toFormat('dd/MM/yyyy')
                : '',
              relationship: subject.legameSoggetto?.descrizioneCodiceLegame || '',
              gender: subject.generalita?.sesso || '',
              aire: subject.generalita?.soggettoAIRE || '',
              birthPlace,
              generality,
              person,
              personInformation: {
                maritalStatus: subject?.statoCivile?.statoCivile || '',
                withoutMaritalStatus: subject?.statoCivile?.statoCivileND === '1' ? true : false,
                maritalStatusNote: subject?.statoCivile?.noteStatoCivile || '',
                qualification: subject.altriDati?.titoloStudio || '',
                jobPosition: subject.altriDati?.posizioneProfessionale || '',
                conditionNotProfessional: subject.altriDati?.condizioneNonProfessionale || '',
              },
            };
          });
      },
    }
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {familyMembers.data ? (
          <TableFamily data={(familyMembers.data as Array<FamilyVM>) || []} />
        ) : undefined}
      </Grid>
    </Grid>
  );
};

FamilyCoexistenceListComponent.displayName = 'Family Coexistence list component';

const FamilyCoexistenceList = memo(FamilyCoexistenceListComponent);

export default FamilyCoexistenceList;
